import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import {
  PriceCalculatorClient,
  PriceCalculatorSettingsDto,
  PriceCalculatorIngredientDto,
  PriceCalculatorRecipeDto,
  CreatePriceCalculatorIngredientDto,
  UpdatePriceCalculatorIngredientDto,
  CreatePriceCalculatorRecipeDto,
  UpdatePriceCalculatorRecipeDto,
  CalculateRecipeDto,
  CalculationResultDto,
  UpdatePriceCalculatorSettingsDto
} from '../../../core/services/service-clients';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

interface PriceCalculatorState {
  settings: PriceCalculatorSettingsDto | null;
  ingredients: PriceCalculatorIngredientDto[];
  recipes: PriceCalculatorRecipeDto[];
  currentRecipe: PriceCalculatorRecipeDto | null;
  calculationResult: CalculationResultDto | null;
  loading: boolean;
  ingredientsLoading: boolean;
  recipesLoading: boolean;
}

const initialState: PriceCalculatorState = {
  settings: null,
  ingredients: [],
  recipes: [],
  currentRecipe: null,
  calculationResult: null,
  loading: false,
  ingredientsLoading: false,
  recipesLoading: false,
};

export const priceCalculatorStore = signalStore(
  withState<PriceCalculatorState>(initialState),

  withComputed((state) => ({
    ingredientsById: computed(() => {
      const map = new Map<number, PriceCalculatorIngredientDto>();
      state.ingredients().forEach(i => map.set(i.id, i));
      return map;
    }),
    recipesById: computed(() => {
      const map = new Map<number, PriceCalculatorRecipeDto>();
      state.recipes().forEach(r => map.set(r.id, r));
      return map;
    }),
  })),

  withMethods((store) => {
    const priceCalculatorClient = inject(PriceCalculatorClient);
    const messageService = inject(MessageService);

    return {
      // Settings
      async loadSettings() {
        patchState(store, { loading: true });
        try {
          const settings = await firstValueFrom(priceCalculatorClient.getSettings());
          patchState(store, { settings, loading: false });
        } catch {
          patchState(store, { loading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Einstellungen konnten nicht geladen werden' });
        }
      },

      async updateSettings(dto: UpdatePriceCalculatorSettingsDto) {
        patchState(store, { loading: true });
        try {
          const settings = await firstValueFrom(priceCalculatorClient.updateSettings(dto));
          patchState(store, { settings, loading: false });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Einstellungen wurden gespeichert' });
        } catch {
          patchState(store, { loading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Einstellungen konnten nicht gespeichert werden' });
        }
      },

      // Ingredients
      async loadIngredients() {
        patchState(store, { ingredientsLoading: true });
        try {
          const ingredients = await firstValueFrom(priceCalculatorClient.getIngredients());
          patchState(store, { ingredients, ingredientsLoading: false });
        } catch {
          patchState(store, { ingredientsLoading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Zutaten konnten nicht geladen werden' });
        }
      },

      async createIngredient(dto: CreatePriceCalculatorIngredientDto) {
        patchState(store, { ingredientsLoading: true });
        try {
          const ingredient = await firstValueFrom(priceCalculatorClient.createIngredient(dto));
          patchState(store, {
            ingredients: [...store.ingredients(), ingredient],
            ingredientsLoading: false
          });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Zutat wurde erstellt' });
          return ingredient;
        } catch {
          patchState(store, { ingredientsLoading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Zutat konnte nicht erstellt werden' });
          return null;
        }
      },

      async updateIngredient(dto: UpdatePriceCalculatorIngredientDto) {
        patchState(store, { ingredientsLoading: true });
        try {
          const ingredient = await firstValueFrom(priceCalculatorClient.updateIngredient(dto.id, dto));
          patchState(store, {
            ingredients: store.ingredients().map(i => i.id === ingredient.id ? ingredient : i),
            ingredientsLoading: false
          });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Zutat wurde aktualisiert' });
          return ingredient;
        } catch {
          patchState(store, { ingredientsLoading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Zutat konnte nicht aktualisiert werden' });
          return null;
        }
      },

      async deleteIngredient(id: number) {
        patchState(store, { ingredientsLoading: true });
        try {
          await firstValueFrom(priceCalculatorClient.deleteIngredient(id));
          patchState(store, {
            ingredients: store.ingredients().filter(i => i.id !== id),
            ingredientsLoading: false
          });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Zutat wurde gelöscht' });
        } catch {
          patchState(store, { ingredientsLoading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Zutat konnte nicht gelöscht werden' });
        }
      },

      // Recipes
      async loadRecipes() {
        patchState(store, { recipesLoading: true });
        try {
          const recipes = await firstValueFrom(priceCalculatorClient.getRecipes());
          patchState(store, { recipes, recipesLoading: false });
        } catch {
          patchState(store, { recipesLoading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Rezepte konnten nicht geladen werden' });
        }
      },

      async loadRecipe(id: number) {
        patchState(store, { loading: true });
        try {
          const recipe = await firstValueFrom(priceCalculatorClient.getRecipe(id));
          patchState(store, { currentRecipe: recipe, loading: false });
          return recipe;
        } catch {
          patchState(store, { loading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Rezept konnte nicht geladen werden' });
          return null;
        }
      },

      async createRecipe(dto: CreatePriceCalculatorRecipeDto) {
        patchState(store, { loading: true });
        try {
          const recipe = await firstValueFrom(priceCalculatorClient.createRecipe(dto));
          patchState(store, {
            recipes: [recipe, ...store.recipes()],
            currentRecipe: recipe,
            loading: false
          });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Rezept wurde erstellt' });
          return recipe;
        } catch {
          patchState(store, { loading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Rezept konnte nicht erstellt werden' });
          return null;
        }
      },

      async updateRecipe(dto: UpdatePriceCalculatorRecipeDto) {
        patchState(store, { loading: true });
        try {
          const recipe = await firstValueFrom(priceCalculatorClient.updateRecipe(dto.id, dto));
          patchState(store, {
            recipes: store.recipes().map(r => r.id === recipe.id ? recipe : r),
            currentRecipe: recipe,
            loading: false
          });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Rezept wurde aktualisiert' });
          return recipe;
        } catch {
          patchState(store, { loading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Rezept konnte nicht aktualisiert werden' });
          return null;
        }
      },

      async deleteRecipe(id: number) {
        patchState(store, { loading: true });
        try {
          await firstValueFrom(priceCalculatorClient.deleteRecipe(id));
          patchState(store, {
            recipes: store.recipes().filter(r => r.id !== id),
            currentRecipe: store.currentRecipe()?.id === id ? null : store.currentRecipe(),
            loading: false
          });
          messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Rezept wurde gelöscht' });
        } catch {
          patchState(store, { loading: false });
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Rezept konnte nicht gelöscht werden' });
        }
      },

      // Calculation
      async calculate(dto: CalculateRecipeDto) {
        try {
          const result = await firstValueFrom(priceCalculatorClient.calculate(dto));
          patchState(store, { calculationResult: result });
          return result;
        } catch {
          messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Berechnung fehlgeschlagen' });
          return null;
        }
      },

      clearCurrentRecipe() {
        patchState(store, { currentRecipe: null, calculationResult: null });
      },

      setCurrentRecipe(recipe: PriceCalculatorRecipeDto | null) {
        patchState(store, { currentRecipe: recipe });
      }
    };
  })
);
